import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { useParams } from "next/navigation";
import { openValidationAlert } from "@/components/ActiveProjects/shared/components/ShowTabValidationAlert";
import { useGlobalStateContext } from "@/context/GlobalContext";
import useLoadProjectDetails from "@/hooks/useLoadProjectDetails";
import { useAuth } from "@/context/AuthContext";
import { getActiveAssessment } from "@/utils/productInfoDetailsSelectors";
import { isProduceRacTjLabelProductSpecificationType } from "@/utils/productSpecificationTypeUtils";


const tabs = {
  compliance: {
    key: "isComplianceDocumentValidated",
    valid: "isComplianceDocumentValidated",
    hasSections: false,
    noChild: true,
  },
  mainDetails: {
    key: "isMainDetailsValidated",
    valid: "isMainDetailsValidated",
    hasSections: false,
    noChild: true,
    hasVendor:true,
  },
  recipe: { key: "recipeRawMaterialsTab", valid: "isRecipeValidate", hasSections: true, editableKey:"recipeRawMaterialSectionEditable" },
  //rawmaterial: { key: "recipeRawMaterialsTab", valid: "isRawMaterialValidated", hasSections: true , editableKey:"recipeRawMaterialSectionEditable"},
  allergens: {
    key: "siteAllergen",
    valid: "isAllergensValidated",
    hasSections: true,
    hasSites: false,
    editableKey:"allergenSectionEditable",
  },
  nutrition: { key: "nutritionTab", valid: "isNutritionValidated", hasSections: true, editableKey:"nutritionSectionEditable" },
  // foodSafety: {
  //   key: "vendorManufacturingFacilities",
  //   valid: "isSiteValidated",
  //   hasSections: false,
  //   hasSites: true,
  //   hasVendor: true,
  // },
  storage: { key: "storageTab", valid: "isStorageValidated", hasSections: true },
  packagingTab: { key: "packagingTab", valid: "isPackagingValidated", hasSections: true, hasVendor:true },
  otherLabeling: { key: "otherLabelingTab", valid: "isOtherLabellingValidated", hasSections: true, hasVendor:true },
};

const specBasedTabs = {
  haba: ["compliance", "mainDetails", "recipe", "storage","packagingTab","otherLabeling"],
  allBrandedProducts: ["compliance", "mainDetails"],
  produceRacBranded: ["compliance", "mainDetails"],
  produceRacTraderJoesLabel: [
    "compliance",
    "mainDetails",
    "recipe",

    "nutrition",
    "storage",
    "packagingTab",
    "otherLabeling",
  ],
  otherProducts: [
    "compliance",
    "mainDetails",
    "recipe",

    "allergens",
    "nutrition",
    "storage",
    "packagingTab",
    "otherLabeling",
  ],
};

const actionTypesSpecificTabs = {
  default: [],
  shareForReview: [],
  requestForRevision: [],
  reviewComplete: [],
  requestNewVersion: [],
  requestUpdates: [],
  /** Launched Products — discontinue product (same cross-tab scope as request new version). */
  requestToDiscontinued: [],
  cancelDiscontinuationRequest: [],
  completeDiscontinuation: [],
  cancelProject: [],
  reviewAgain: []
};

interface APValidationContextValue {
  validations: any;
  validationActive: boolean;
  setValidationActive: (attempted: boolean) => void;
  setValidations: React.Dispatch<React.SetStateAction<any>>;
  killValidation: boolean;
  setKillValidation: (kill: boolean) => void;
}

const APValidationContext = createContext<APValidationContextValue>({
  validations: undefined,
  validationActive: false,
  setValidationActive: () => {},
  setValidations: () => {},
  killValidation: false,
  setKillValidation: () => {},
});
const toCamelCase = (input: string): string => {
  return input
    .replace(/&/g, " ") // remove &
    .replace(/[^a-zA-Z0-9 ]/g, "") // remove other special chars
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join("");
};

const getSpecType = (specificationType: string | undefined): string | undefined => {
  const normalized = toCamelCase(String(specificationType || ""));
  if (
    normalized === "habaHealthAndBeautyAids" ||
    normalized === "haba" ||
    normalized.toLowerCase().includes("haba")
  ) {
    return "haba";
  } else {
    return normalized;
  }
};

const getTabs = (specificationType: string | undefined, actionType: string = "save"): any => {
  const normalized = getSpecType(specificationType);
  const filteredTabsBasedOnSpec =
    specBasedTabs[normalized as keyof typeof specBasedTabs] || specBasedTabs.otherProducts || [];
  // Start with spec-based tab keys
  let tabKeys: string[] = [...filteredTabsBasedOnSpec];

  // Get action-specific tabs (e.g., foodSafety for certain actions)
  const actionTypeSpecificTabs =
    actionTypesSpecificTabs?.[actionType as keyof typeof actionTypesSpecificTabs] || [];

  // Add action-specific tabs if they don't already exist
  actionTypeSpecificTabs.forEach(tabKey => {
    if (!tabKeys.includes(tabKey) && tabKey in tabs) {
      tabKeys.push(tabKey);
    }
  });

  // Build the result object with the same structure as tabs
  const result: any = {};
  tabKeys.forEach(key => {
    if (key in tabs) {
      result[key] = tabs[key as keyof typeof tabs];
    }
  });

  return result;
};

type CustomValidationType = {
  tabId: string;
  section: {
    id: string;
    valid: boolean;
  }[];
};


const parseValidationValue = (
  validationValue: any,
  userRole: string | undefined,
  hasVendor: boolean
): boolean => {

  if (!hasVendor) {

    if (typeof validationValue === "string") {
      return validationValue.toLowerCase() === "true";
    }
  
    return Boolean(validationValue);
  }


  if (!userRole) {

    return false;
  }


  const normalizedRole = userRole.toUpperCase() === "VENDOR" ? "Vendor" : "TJ";


  if (validationValue === null || validationValue === undefined || validationValue === "") {
    return false;
  }


  if (typeof validationValue === "string") {
 
    const trimmed = validationValue.trim();
    if (trimmed === "" || trimmed === "{}") {
      return false;
    }

    try {
      const parsed = JSON.parse(validationValue);

      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
    
        if (Object.keys(parsed).length === 0) {
          return false;
        }
       
        if (parsed[normalizedRole] !== undefined) {
          return Boolean(parsed[normalizedRole]);
        }
       
        return false;
      }
      
      if (typeof parsed === "boolean") {
        return parsed;
      }
      if (typeof parsed === "string") {
        return parsed.toLowerCase() === "true";
      }
      
      return Boolean(parsed);
    } catch {
     
      return validationValue.toLowerCase() === "true";
    }
  }

  
  if (typeof validationValue === "object" && validationValue !== null && !Array.isArray(validationValue)) {
  
    if (Object.keys(validationValue).length === 0) {
      return false;
    }
   
    if (validationValue[normalizedRole] !== undefined) {
      return Boolean(validationValue[normalizedRole]);
    }
   
    return false;
  }

 
  return Boolean(validationValue);
};

const toBoolean = (value: any) => {
  if (value === true || value === false) return value;

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1";
  }

  return false;
};

// Compute validations from productInfo
export const computeValidations = (
  productInfo: any,
  activeVendorTab: number = 0,
  activeVendorVersionIndex: number = 0,
  actionType: string = "save",
  customValidation?: CustomValidationType | undefined,
  userRole?: string | undefined
): any => {
  if (
    !productInfo?.vendorDetails ||
    !Array.isArray(productInfo.vendorDetails) ||
    Object.keys(tabs).length === 0
  ) {
    return {};
  }
  const merged = getActiveAssessment(
    productInfo as Record<string, unknown> | null | undefined,
    activeVendorTab,
    activeVendorVersionIndex
  );
  const selectedVendor = merged ? [merged] : [];
  if (!selectedVendor || selectedVendor.length === 0 || !selectedVendor[0]) {
    return {};
  }

  const specType = String(productInfo?.productSpecificationType ?? productInfo?.category ?? "");
  const shouldApplyEditableFlagSkipping = isProduceRacTjLabelProductSpecificationType(specType);

  let validationObj: any = {};
  const tabsToValidate = getTabs(productInfo?.productSpecificationType, actionType);
  if (!tabsToValidate) {
    return {};
  }
  selectedVendor?.forEach((vendor: any) => {
    if (!vendor) return;

    Object.entries(tabsToValidate).forEach(([i, value]: [string, any]) => {
      if (!value?.key) return;

      // Skip validation entirely for tabs where info is not required.
      // - recipeRawMaterialSectionEditable=false should skip BOTH "recipe" + "rawmaterial" tabs (they share the same toggle).
      if (shouldApplyEditableFlagSkipping) {
        const editableKey = value?.editableKey;
        if (typeof editableKey === "string" && editableKey.length > 0) {
          if (vendor?.[editableKey] === false) return;
        }
      }

      const sections = vendor?.[value?.key] || [];
      const sites = vendor?.[value?.key] || [];
      // Initialize validationObj[key] if it doesn't exist
      if (!validationObj[i] && vendor?.assessmentId) {
        validationObj[i] = {
          vendors: {
            [vendor.assessmentId]: {
              validated: false,
              ...(value?.hasSections && { sections: {} }),
              ...(value?.hasSites && { sites: {} }),
            },
          },
          validated: false,
        };
      }
      if (validationObj[i] && vendor?.assessmentId) {
        if (value?.hasSections === true && Array.isArray(sections)) {
          sections.forEach((section: any) => {
            if (!section || !section.id) return;

            // Skip this section if it's in the skip list for this tab
            let isValidated = false;
            if (validationObj?.[i]?.vendors?.[vendor.assessmentId]?.sections) {
              if (
                customValidation &&
                customValidation?.tabId === i &&
                Array.isArray(customValidation?.section) &&
                customValidation?.section.length > 0 && 
                customValidation?.section?.some(
                  s => String(s.id) === String(section.id)
                )
              ) {
                isValidated =
                  toBoolean(customValidation?.section.find((s: any) => s.id === section.id)?.valid) || false;
              } else {
                // Check if hasVendor is true, use role-based parsing
                if (value?.hasVendor === true) {
                  isValidated = parseValidationValue(section?.[value?.valid], userRole, true);
                } else {
                  isValidated = toBoolean(section?.[value?.valid]) || false;
                }
              }

              validationObj[i].vendors[vendor.assessmentId].sections[section.id] = isValidated;
            }
          });
        } else if (value?.hasSites === true && Array.isArray(sites)) {
          sites.forEach((site: any) => {
            if (!site || !site.id) return;

            if (validationObj?.[i]?.vendors?.[vendor.assessmentId]?.sites) {
              let isValidated = false;
              // Skip this section if it's in the skip list for this tab
              if (
                customValidation &&
                customValidation?.tabId === i &&
                Array.isArray(customValidation?.section) &&
                customValidation?.section.length > 0 &&
                customValidation?.section?.some(
                  s => String(s.id) === String(site.id)
                )
              ) {
                isValidated =
                  toBoolean(customValidation?.section.find((s: any) => s.id === site.id)?.valid) || false;
              } else {
                if (value?.hasVendor === true) {
                  isValidated = parseValidationValue(site?.[value?.valid], userRole, true);
                } else {
                  isValidated = toBoolean(site?.[value?.valid]) || false;
                }
              }
              validationObj[i].vendors[vendor.assessmentId].sites[site.id] = isValidated;
            }
          });
        }

        // After processing all sections, check if ALL sections are true
        // If ANY section is false, error = false. If ALL sections are true, error = true
        if (
          validationObj[i].vendors[vendor.assessmentId] &&
          validationObj[i]?.vendors[vendor.assessmentId]?.validated == false
        ) {
          const vendorSections = validationObj?.[i]?.vendors?.[vendor?.assessmentId]?.sections;
          const vendorSites = validationObj?.[i]?.vendors?.[vendor?.assessmentId]?.sites;
          if (
            value?.hasSections === true &&
            vendorSections &&
            Object.keys(vendorSections).length > 0
          ) {
            const allSectionsTrue = Object.values(vendorSections).every((val: any) => val === true || val === "true");
            validationObj[i].vendors[vendor.assessmentId].validated = allSectionsTrue;
            if (allSectionsTrue) {
              validationObj[i].validated = true;
            }
          }
          if (value?.hasSites === true && vendorSites && Object.keys(vendorSites).length > 0) {
            const allSitesTrue = Object.values(vendorSites).every((val: any) => val === true || val === "true");
            validationObj[i].vendors[vendor.assessmentId].validated = allSitesTrue;
            if (allSitesTrue) {
              validationObj[i].validated = true;
            }
          }
          if (value?.noChild === true && validationObj[i]?.vendors[vendor.assessmentId]) {
            // Check if hasVendor is true, use role-based parsing
            let isValid = false;
            if (value?.hasVendor === true) {
              isValid = parseValidationValue(vendor?.[value?.valid], userRole, true);
            } else {
              // Handle string "true"/"false" for non-vendor cases
              const validationValue = vendor?.[value?.valid];
              if (typeof validationValue === "string") {
                isValid = toBoolean(validationValue.toLowerCase() === "true");
              } else {
                isValid = toBoolean(validationValue);
              }
            }
            if(customValidation &&
              customValidation?.tabId === i &&
              Array.isArray(customValidation?.section) &&
              customValidation?.section.length > 0)
            {
              const customSection = customValidation?.section.find(
                (s: any) => s.id === String(vendor?.assessmentId)
              );
              if (customSection !== undefined) {
                const isValidated = toBoolean(customSection?.valid);
                validationObj[i].vendors[vendor.assessmentId].validated = isValidated;
                if (isValidated) {
                  validationObj[i].validated = true;
                }
              } else {
                validationObj[i].vendors[vendor.assessmentId].validated = isValid;
                if (isValid) {
                  validationObj[i].validated = true;
                }
              }
            } 
            else {
              validationObj[i].vendors[vendor.assessmentId].validated = isValid;
              if (isValid) {
                validationObj[i].validated = true;
              }
            }
          }
        } 
      }
    });
  });

  return validationObj;
};

export const APValidationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [validationActive, setValidationActive] = useState<boolean>(false);

  const [validations, setValidations] = useState<any>({});

  const [killValidation, setKillValidation] = useState<boolean>(false);

  const value = useMemo<APValidationContextValue>(
    () => ({
      validations,
      validationActive,
      setValidationActive,
      killValidation,
      setKillValidation,
      setValidations,
    }),
    [validations, validationActive, killValidation]
  );

  return <APValidationContext.Provider value={value}>{children}</APValidationContext.Provider>;
};

const useActiveProjectValidation = () => {
  const params = useParams();
  // const id = params?.id as string | undefined;
  const { loadProjectDetails } = useLoadProjectDetails();
  const { activeProjectsProductInfo, activeVendorTab, activeVendorVersionIndex } = useGlobalStateContext();
  const paramIdeaId = params?.id as string | undefined;
  const id = activeProjectsProductInfo?.ideaId || paramIdeaId || "";
  const { userDetails } = useAuth();
  const { validations, validationActive, setValidationActive, setValidations, killValidation, setKillValidation } =
    useContext(APValidationContext);
  
  // Get user role for role-based validation
  const userRole = userDetails?.type?.toUpperCase() || undefined;

  // Helper function to recalculate vendor and tab errors
  const recalculateErrors = useCallback((updatedValidations: any, tabId: string) => {
    if (!updatedValidations?.[tabId]?.vendors || !tabId) return updatedValidations;

    const vendors = updatedValidations[tabId].vendors;
    if (!vendors || typeof vendors !== "object") return updatedValidations;

    // Recalculate vendor errors
    Object.keys(vendors).forEach(assessmentIdStr => {
      if (!assessmentIdStr || !vendors[assessmentIdStr]) return;

      const vendorSections = vendors[assessmentIdStr]?.sections;
      const vendorSites = vendors[assessmentIdStr]?.sites;

      if (
        vendorSections &&
        typeof vendorSections === "object" &&
        Object.keys(vendorSections).length > 0
      ) {
        const allSectionsTrue = Object.values(vendorSections).every((val: any) => val === true || val === "true");
        vendors[assessmentIdStr].validated = allSectionsTrue;
      }
      if (vendorSites && typeof vendorSites === "object" && Object.keys(vendorSites).length > 0) {
        const allSitesTrue = Object.values(vendorSites).every((val: any) => val === true || val === "true");
        vendors[assessmentIdStr].validated = allSitesTrue;
      }
    });

    // Recalculate tab error
    const allVendorsValidated = Object.values(vendors).every(
      (vendor: any) => vendor?.validated === true
    );
    updatedValidations[tabId].validated = allVendorsValidated;

    return updatedValidations;
  }, []);

  // Function to forcefully update validation flag for a section
  const updateSectionValidationFlag = useCallback(
    (tabId: string, sectionId: string | number) => {
      if (!tabId || sectionId === null || sectionId === undefined) return;

      const sectionIdStr = String(sectionId);
      setValidations((prev: any) => {
        if (!prev?.[tabId]?.vendors || !prev[tabId]?.vendors) return prev;

        const updated = JSON.parse(JSON.stringify(prev)); // Deep clone

        // Update section in all vendors
        Object.keys(updated[tabId].vendors || {}).forEach(assessmentIdStr => {
          if (!assessmentIdStr || !updated[tabId].vendors[assessmentIdStr]) return;

          if (updated[tabId].vendors[assessmentIdStr]?.sections?.[sectionIdStr] !== undefined) {
            updated[tabId].vendors[assessmentIdStr].sections[sectionIdStr] = true;
          }
        });

        // Recalculate vendor and tab errors
        return recalculateErrors(updated, tabId);
      });
    },
    [setValidations, recalculateErrors]
  );

  const updateSiteValidationFlag = useCallback(
    (tabId: string, siteId: string | number) => {
      if (!tabId || siteId === null || siteId === undefined) return;

      const siteIdStr = String(siteId);
      setValidations((prev: any) => {
        if (!prev?.[tabId]?.vendors || !prev[tabId]?.vendors) return prev;

        const updated = JSON.parse(JSON.stringify(prev)); // Deep clone

        // Update section in all vendors
        Object.keys(updated[tabId].vendors || {}).forEach(assessmentIdStr => {
          if (!assessmentIdStr || !updated[tabId].vendors[assessmentIdStr]) return;

          if (updated[tabId].vendors[assessmentIdStr]?.sites?.[siteIdStr] !== undefined) {
            updated[tabId].vendors[assessmentIdStr].sites[siteIdStr] = true;
          }
        });

        // Recalculate vendor and tab errors
        return recalculateErrors(updated, tabId);
      });
    },
    [setValidations, recalculateErrors]
  );

  const updateVendorValidationFlag = useCallback(
    (tabId: string, assessmentId: string | number) => {
      if (!tabId || assessmentId === null || assessmentId === undefined) return;

      const assessmentIdStr = String(assessmentId);
      setValidations((prev: any) => {
        if (!prev?.[tabId]?.vendors?.[assessmentIdStr]) return prev;

        const updated = JSON.parse(JSON.stringify(prev)); // Deep clone

        if (updated[tabId]?.vendors?.[assessmentIdStr]) {
          updated[tabId].vendors[assessmentIdStr].validated = true;
        }

        // Recalculate tab error
        return recalculateErrors(updated, tabId);
      });
    },
    [setValidations, recalculateErrors]
  );

  /**
   * Clear all validation errors for a tab (sections + sites) so red dots/highlights disappear.
   * Used when "Info Required" toggles are set to "No" (Produce RAC - TJ Label).
   */
  const clearTabValidation = useCallback(
    (tabId: string) => {
      if (!tabId) return;
      setValidations((prev: any) => {
        if (!prev?.[tabId]?.vendors) return prev;
        const updated = JSON.parse(JSON.stringify(prev));
        const vendors = updated?.[tabId]?.vendors;
        if (!vendors || typeof vendors !== "object") return prev;

        Object.keys(vendors).forEach((assessmentIdStr) => {
          const v = vendors?.[assessmentIdStr];
          if (!v || typeof v !== "object") return;
          if (v.sections && typeof v.sections === "object") {
            Object.keys(v.sections).forEach((sectionId) => {
              v.sections[sectionId] = true;
            });
          }
          if (v.sites && typeof v.sites === "object") {
            Object.keys(v.sites).forEach((siteId) => {
              v.sites[siteId] = true;
            });
          }
          v.validated = true;
        });

        updated[tabId].validated = true;
        return updated;
      });
    },
    [setValidations]
  );

  const deleteValidationSection = useCallback(
    (tabId: string, sectionId: string | number) => {
      if (!tabId || sectionId === null || sectionId === undefined) return;

      const sectionIdStr = String(sectionId);
      setValidations((prev: any) => {
        if (!prev?.[tabId]?.vendors || !prev[tabId]?.vendors) return prev;

        const updated = JSON.parse(JSON.stringify(prev)); // Deep clone

        // Remove section from all vendors
        Object.keys(updated[tabId].vendors || {}).forEach(assessmentIdStr => {
          if (!assessmentIdStr || !updated[tabId].vendors[assessmentIdStr]) return;

          if (updated[tabId].vendors[assessmentIdStr]?.sections?.[sectionIdStr] !== undefined) {
            delete updated[tabId].vendors[assessmentIdStr].sections[sectionIdStr];
          }
        });

        // Recalculate vendor and tab errors
        return recalculateErrors(updated, tabId);
      });
    },
    [setValidations, recalculateErrors]
  );

  // Returns true if tab has error, false if no error
  const hasTabError = useMemo(() => {
    return (tabId: string): boolean => {
      if (!validationActive || !validations || !tabId || !validations[tabId]) return false;
      const validated = validations[tabId]?.validated || false;
      return validated !== true;
    };
  }, [validations, validationActive]);

  // Returns true if vendor has error, false if no error
  const hasVendorError = useMemo(() => {
    return (tabId: string, assessmentId: string | number | null | undefined): boolean => {
      if (!validationActive || !assessmentId || !validations || !tabId || !validations[tabId])
        return false;
      const assessmentIdStr = String(assessmentId);

      if (!assessmentIdStr) return false;

      // Only validate if vendor exists in the validation object
      const vendor = validations[tabId]?.vendors?.[assessmentIdStr];
      if (!vendor) return false; // Vendor doesn't exist, don't show error

      const validated = vendor?.validated;
      return validated !== true;
    };
  }, [validations, validationActive]);

  // Returns true if specific section ID has error, false if no error
  // If assessmentId is not provided, searches across all vendors in the tab
  const hasSectionError = useMemo(() => {
    return (tabId: string, sectionId: string | number): boolean => {
      if (
        !validationActive ||
        !validations ||
        !tabId ||
        !validations[tabId] ||
        sectionId === null ||
        sectionId === undefined
      )
        return false;
      const sectionIdStr = String(sectionId);

      if (!sectionIdStr) return false;

      // If assessmentId is not provided, search all vendors in the tab for this section
      const vendors = validations[tabId]?.vendors;
      if (!vendors || typeof vendors !== "object") return false;

      // Check if any vendor has this section with an error
      return Object.values(vendors).some((vendor: any) => {
        if (!vendor?.sections || typeof vendor.sections !== "object") return false;
        const isValidated = vendor.sections[sectionIdStr];
        // Return true if section exists and is not validated (has error)
        return isValidated === false;
      });
    };
  }, [validations, validationActive]);

  const hasSiteError = useMemo(() => {
    return (tabId: string, site: string | number): boolean => {
      if (
        !validationActive ||
        !validations ||
        !tabId ||
        !validations[tabId] ||
        site === null ||
        site === undefined
      )
        return false;
      const siteStr = String(site);

      if (!siteStr) return false;

      // If assessmentId is not provided, search all vendors in the tab for this section
      const vendors = validations[tabId]?.vendors;
      if (!vendors || typeof vendors !== "object") return false;

      // Check if any vendor has this section with an error
      return Object.values(vendors).some((vendor: any) => {
        if (!vendor?.sites || typeof vendor.sites !== "object") return false;
        const isValidated = vendor.sites[siteStr];
        // Return true if section exists and is not validated (has error)
        return isValidated === false;
      });
    };
  }, [validations, validationActive]);

  const isAllValidated = useCallback(
    async (actionType: string, customValidation?: CustomValidationType | undefined): Promise<boolean> => {
      // Get latest project data from API
      if (!id || !actionType) {
        console.warn("⚠️ [isAllValidated] Missing required parameters", { id, actionType });
        return false;
      }
      if (killValidation && actionType === "default") {
        setValidationActive(false);
        setValidations({});
        return false;
      }

      try {
        // Call API - this updates activeProjectsProductInfo in global context
        let loadedProject: any;
        if(actionType == "default" && activeProjectsProductInfo?.ideaId) {
          loadedProject = activeProjectsProductInfo;
        }
        else {
          loadedProject = await loadProjectDetails(id.toString());
        }
          
          if (!loadedProject) {
            console.warn("⚠️ [isAllValidated] Failed to load project details");
            return false;
          }
        const freshValidations = computeValidations(
          loadedProject,
          activeVendorTab,
          activeVendorVersionIndex ?? 0,
          actionType,
          customValidation || undefined,
          userRole
        );

        if (!freshValidations) {
          console.log("✅ [isAllValidated] No validations computed");
          return false;
        }

        setValidations(freshValidations);

        if (
          !freshValidations ||
          typeof freshValidations !== "object" ||
          Object.keys(freshValidations).length === 0
        ) {
          console.log("✅ [isAllValidated] No validations to check");
          return false;
        }

        let allValid = Object.values(freshValidations).every((tab: any) => tab?.validated === true);

        // if (!allValid) {
          setValidationActive(true);
        // }

        return allValid;
      } catch (error) {
        console.error("❌ [isAllValidated] Error fetching project details:", error);
        return false;
      }
    },
    [
      id,
      loadProjectDetails,
      activeProjectsProductInfo,
      setValidationActive,
      setValidations,
      activeVendorTab,
      activeVendorVersionIndex,
      userRole,
    ]
  );

  const clearValidation = useCallback(() => {
    setValidations({});
    setValidationActive(false);
  }, [setValidations, setValidationActive]);

  const killValidationHandler = useCallback(() => {
    setKillValidation(true);
  }, [setKillValidation]);

  /**
   * Opens the confirmation alert (OK button only): `showValidationAlert()` or `showValidationAlert("message")`.
   * Its open state lives in ShowTabValidationAlert.tsx; the alert is rendered once in
   * `ActiveProjectDetail/staticTabs.tsx`.
   */
  const showValidationAlert = openValidationAlert;
  
  return {
    validations,
    validationActive,
    setValidationActive,
    isAllValidated,
    hasTabError,
    hasVendorError,
    hasSectionError,
    updateSectionValidationFlag,
    updateVendorValidationFlag,
    deleteValidationSection,
    hasSiteError,
    updateSiteValidationFlag,
    clearTabValidation,
    clearValidation,
    killValidation,
    killValidationHandler,
    showValidationAlert,
  };
};

export default useActiveProjectValidation;
